import type { RowObject } from '@highcharts/dashboards/es-modules/Data/DataTable.js';
import type {
    DashboardQueryColumnsOf,
    DashboardQueryKey,
} from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';
import type {
    GridColumnDefinition,
    GridColumnEntry,
    GridColumnSettings,
} from '@h20-shared/grids/columns';

/** The column keys a query returns, such as `'patientCount'` for `exampleCount`. */
export type QueryColumnKey<TKey extends DashboardQueryKey> =
    DashboardQueryColumnsOf<TKey>[number]['key'];

/** A dashboard row is a plain object of primitive cells, which is what Highcharts hands the grid. */
export type DashboardColumnSettings = GridColumnSettings<RowObject>;

/** One column of one query, as that query's column file declared it. */
export type DashboardColumnDefinition<TKey extends DashboardQueryKey> = GridColumnDefinition<
    QueryColumnKey<TKey>,
    RowObject
> & { readonly queryKey: TKey };

/** One column as a query's column file writes it, where the name must be one the query returns. */
export type DashboardColumnEntry<TKey extends DashboardQueryKey> = GridColumnEntry<
    QueryColumnKey<TKey>,
    RowObject
>;

/** Declares the settings for one query's columns, where a column left out keeps the defaults for its type. */
export function defineGridColumns<TKey extends DashboardQueryKey>(
    queryKey: TKey,
    columns: readonly DashboardColumnEntry<TKey>[]
): DashboardColumnDefinition<TKey>[] {
    return columns.map(([columnKey, settings = {}]) => ({ queryKey, columnKey, settings }));
}
